import type { Middleware } from '@reduxjs/toolkit';
import type { RootState } from 'src/state';

import { paths } from 'src/routes/paths';

import * as ActionTypes from 'src/state/action-types';

/**
 * Workspace middleware handles side effects:
 * - Saving workspace ID to localStorage
 * - Navigation when workspace is selected/created
 */
export const workspaceMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    // Call the reducer first so state is updated
    const result = next(action);

    // Handle side effects after state update
    switch (action.type) {
      case ActionTypes.WORKSPACE_WORKSPACE_SELECTED: {
        const { workspaceId } = (action as ActionTypes.IWorkspaceSelectedAction).payload;

        // Side effect: Save to localStorage
        try {
          localStorage.setItem('lastWorkspaceId', workspaceId);
        } catch (error) {
          console.error('Failed to save workspace to localStorage:', error);
        }

        // Side effect: Navigate to root folder of selected workspace
        // We need to check if window exists (for SSR compatibility)
        if (typeof window !== 'undefined' && window.history) {
          const targetPath = paths.dashboard.content(workspaceId, 'root');
          window.history.replaceState(null, '', targetPath);

          // Dispatch navigation event for app tracking
          const event = new PopStateEvent('popstate');
          window.dispatchEvent(event);
        }
        break;
      }

      case ActionTypes.WORKSPACE_WORKSPACE_CREATED: {
        const { workspace } = (action as ActionTypes.IWorkspaceCreatedAction).payload;

        // Side effect: Save to localStorage
        try {
          localStorage.setItem('lastWorkspaceId', workspace.id);
        } catch (error) {
          console.error('Failed to save workspace to localStorage:', error);
        }

        // Side effect: Navigate to root folder of newly created workspace
        if (typeof window !== 'undefined' && window.history) {
          const targetPath = paths.dashboard.content(workspace.id, 'root');
          window.history.replaceState(null, '', targetPath);

          // Dispatch navigation event for app tracking
          const event = new PopStateEvent('popstate');
          window.dispatchEvent(event);
        }
        break;
      }

      default:
        break;
    }

    return result;
  };
